import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import {
  Stack,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import { useLocation } from 'react-router-dom';
import NoteCard from '@/components/NoteCard/NoteCard';
import NoteListRow from '@/components/NoteListRow/NoteListRow';
import NoteListHeader from '@/components/NoteListHeader/NoteListHeader';
import SkeletonListRow from '@/components/NoteListRow/SkeletonListRow';
import useAlertStore from '@/stores/alertStore';
import emptyNotes from '@/assets/empty-notes.png';
import SkeletonCard from '@/components/NoteCard/SkeletonCard';
import { fetchGetMyNotesDirect, fetchSearchNotes, useDeleteNote } from '@/services/noteGoApi/noteGoApiComponents';
import { useAuthStore } from '@/stores/authStore';
import { NoteOut, SortByEnum, SortOrderEnum, ProcessingTypeEnum } from '@/services/noteGoApi/noteGoApiSchemas';
import { useSearchFilterNotesStore } from '@/stores/searchFilterNotesStore';
import { filtersToProcessingType } from '@/helpers/filters';
import { matchesOwner } from '@/helpers/ownerName';
import { API_SORT_FIELDS, type NoteSortField } from '@/constants/noteListColumns';
import FilterTags from '../FilterTags/FilterTags';
import DateRangeFilter from '../NotesHeader/DateRangeFilter';
import ViewToggle from '../NotesHeader/ViewToggle';
import ListViewControls from '../NotesHeader/ListViewControls';
import { useBannerStore } from '@/stores/bannerStore';
import { errorReporter } from '@/services/errorReportingService';
import { RouteMap } from '@/routes/Routes';
import { useTagCounts } from '@/hooks/useTagCounts';
import { useCurrentPlanFeatures } from '@/hooks/useCurrentPlanFeatures';
import { useNavigate } from 'react-router-dom';
import ExpandableSearch from '../NotesHeader/ExpandableSearch';
import { useOrganizationNotes } from '@/features/betaOrganizations/hooks/useOrganizationNotes';
import { useOrganizationFolders } from '@/features/betaOrganizations/hooks/useOrganizationFolders';
import { useOrganizations } from '@/features/betaOrganizations/hooks/useOrganizations';
import { BETA_ORGANIZATIONS_FEATURE_ENABLED } from '@/features/betaOrganizations/config';
import { reconcileNoteFolder } from '@/features/betaOrganizations/utils/folderMembership';
import FolderShareModal from '@/features/betaOrganizations/components/FolderShareModal';
import TeamShareFolderModal from '@/features/betaOrganizations/components/TeamShareFolderModal';
import type { Organization, OrganizationFolder } from '@/features/betaOrganizations/types';
import { getTagName } from '@/utils/tagUtils';
import { getDeleteNoteErrorMessage } from '@/utils/deleteNoteError';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';

const NOTES_PER_BATCH = 20;

interface NotesContentProps {
  shared?: boolean;
  organizationId?: string;
  onRemoveFromOrganization?: (noteId: string) => void;
  canRemoveFromOrganization?: boolean;
  /**
   * Org-mode folder filter: restrict the displayed org notes to this set of
   * note ids. `null`/`undefined` shows all org notes (no folder filter).
   */
  noteIdFilter?: string[] | null;
  /** Org-mode display name of the current folder (or "General") for empty-state copy. */
  folderName?: string;
}

const NotesContent = ({
  shared = false,
  organizationId,
  onRemoveFromOrganization,
  canRemoveFromOrganization = false,
  noteIdFilter,
  folderName,
}: NotesContentProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userToken, userData } = useAuthStore();
  const showAlert = useAlertStore((state) => state.showAlert);
  const {
    searchQuery,
    selectedFilters,
    sortBy,
    sortOrder,
    selectedTags,
    viewMode,
    listViewPreferences,
    dateRange,
    setSortBy,
    setSortOrder,
  } = useSearchFilterNotesStore();
  const { mutateAsync: deleteNote } = useDeleteNote();
  const location = useLocation();
  const navigate = useNavigate();

  const isOrgMode = !!organizationId;
  const isFavoritesView = location.pathname === RouteMap.FAVORITES;
  const [tagFilterOpened, setTagFilterOpened] = useState(false);
  // CONTRACT #3: counts must match the notes this view actually lists — the
  // favorites view filters on `favorite: true`, so the counts must too.
  const { tagCounts: searchTagCounts } = useTagCounts({
    shared,
    favorite: isFavoritesView ? true : undefined,
    enabled: tagFilterOpened && !isOrgMode,
  });
  const { features, isLoading: planLoading } = useCurrentPlanFeatures();

  const {
    organization: orgData,
    notes: orgNotes,
    isLoading: isOrgLoading,
    error: orgNotesError,
    refetch: refetchOrgNotes,
  } = useOrganizationNotes(organizationId, isOrgMode);

  const {
    folders: orgFolders,
    addNoteToFolder,
    removeNoteFromFolder,
    refetch: refetchOrgFolders,
  } = useOrganizationFolders(organizationId, isOrgMode);

  const [apiNotes, setApiNotes] = useState<NoteOut[]>([]);
  const [isApiLoading, setIsApiLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(NOTES_PER_BATCH);
  const gridRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // "Move to folder" dialog (org mode only).
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveNoteId, setMoveNoteId] = useState<string | null>(null);
  const [moveTargetFolderId, setMoveTargetFolderId] = useState<string>('general');
  const [isMovingFolder, setIsMovingFolder] = useState(false);
  // Inline "New folder" creation from within the Add-to-folder dialog.
  const [createFolderOpen, setCreateFolderOpen] = useState(false);

  const notes = isOrgMode ? orgNotes : apiNotes;
  const isLoading = isOrgMode ? isOrgLoading : isApiLoading;

  // "Team" badge for the personal My Notes view: a note is a team note when its
  // id appears in any of the user's teams. Derived client-side from the teams'
  // note_ids (the backend's owning_organization_id flag isn't on NoteOut yet).
  // Skipped in org mode (every note is already a team note) and the shared view.
  const showTeamBadge = BETA_ORGANIZATIONS_FEATURE_ENABLED && !isOrgMode && !shared;
  const { organizations } = useOrganizations(showTeamBadge);
  const teamNoteIds = useMemo(() => {
    const ids = new Set<string>();
    if (showTeamBadge) organizations.forEach((org) => org.note_ids?.forEach((nid) => ids.add(nid)));
    return ids;
  }, [showTeamBadge, organizations]);

  // Team the row menu can move a personal note into / out of. Mirrors NoteHeader:
  // prefer a team the user owns, else the first team they belong to.
  const team = useMemo(
    () => organizations.find((org) => org.owner_id === userData?.firebase_uid) ?? organizations[0],
    [organizations, userData?.firebase_uid],
  );
  const canMoveToTeam = showTeamBadge && !!team;
  // Snapshot the resolved team + note when the modal opens so a background
  // `organizations` refetch can't unmount the modal mid-flow (PR review #804).
  const [teamModalTarget, setTeamModalTarget] = useState<{ noteId: string; organization: Organization } | null>(null);
  const handleOpenTeamShare = useCallback(
    (noteId: string) => {
      if (team) setTeamModalTarget({ noteId, organization: team });
    },
    [team],
  );

  const { headerHeight } = useBannerStore();

  const isListView = viewMode === 'list';

  const processingType = filtersToProcessingType(selectedFilters);

  // Determine API sort params - only pass fields the API understands
  const apiSortBy = API_SORT_FIELDS.has(sortBy) ? sortBy : 'created_at';
  const apiSortOrder = API_SORT_FIELDS.has(sortBy) ? sortOrder : 'desc';

  // Org mode computes tag counts from the loaded org notes (no separate fetch).
  // Scope to the current folder via noteIdFilter so counts match the visible
  // list (CONTRACT #3: tag counts must be scoped to the current view).
  const orgTagCounts = useMemo(() => {
    if (!isOrgMode) return {};
    const counts: Record<string, number> = {};
    const allowed = noteIdFilter ? new Set(noteIdFilter) : null;
    orgNotes.forEach((note) => {
      if (allowed && !allowed.has(note.id)) return;
      note.tags?.forEach((tag) => {
        const name = getTagName(tag).trim().toLowerCase();
        if (name) counts[name] = (counts[name] || 0) + 1;
      });
    });
    return counts;
  }, [isOrgMode, orgNotes, noteIdFilter]);

  const accurateTagCounts = isOrgMode ? orgTagCounts : searchTagCounts;

  // Client-side date range filtering + metadata sorting
  const filteredNotes = useMemo(() => {
    let result = notes;

    // Org mode: API doesn't accept search/processing_type/tags/favorite params,
    // so filter client-side to mirror My Notes behavior exactly.
    if (isOrgMode) {
      // Folder filter: restrict to notes in the selected folder (or "not filed"
      // set). null/undefined means no folder filter (show all org notes).
      if (noteIdFilter) {
        const allowed = new Set(noteIdFilter);
        result = result.filter((n) => allowed.has(n.id));
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        result = result.filter((n) => {
          const inTitle = n.title?.toLowerCase().includes(q);
          const inSummary = n.summary?.toLowerCase().includes(q);
          const inTags = n.tags?.some((t) => getTagName(t).toLowerCase().includes(q));
          return inTitle || inSummary || inTags || matchesOwner(n.shared_by, q);
        });
      }
      if (processingType) {
        result = result.filter((n) => n.processing_type === processingType);
      }
      if (selectedTags.length > 0) {
        const wanted = selectedTags.map((t) => getTagName(t).toLowerCase());
        result = result.filter((n) => wanted.every((w) => n.tags?.some((t) => getTagName(t).toLowerCase() === w)));
      }
      if (isFavoritesView) {
        result = result.filter((n) => n.is_favorite);
      }

      // Apply API-equivalent sort (created_at/title) client-side in org mode
      if (API_SORT_FIELDS.has(sortBy)) {
        const asc = sortOrder === 'asc';
        result = [...result].sort((a, b) => {
          if (sortBy === 'title') {
            const ta = (a.title || '').toLowerCase();
            const tb = (b.title || '').toLowerCase();
            return asc ? ta.localeCompare(tb) : tb.localeCompare(ta);
          }
          // created_at
          const da = a.created_at ? new Date(a.created_at).getTime() : 0;
          const db = b.created_at ? new Date(b.created_at).getTime() : 0;
          return asc ? da - db : db - da;
        });
      }
    }

    if (dateRange.start) {
      const startDate = new Date(dateRange.start + 'T00:00:00');
      result = result.filter((note) => {
        if (!note.created_at) return true;
        return new Date(note.created_at) >= startDate;
      });
    }
    if (dateRange.end) {
      const endDate = new Date(dateRange.end + 'T23:59:59');
      result = result.filter((note) => {
        if (!note.created_at) return true;
        return new Date(note.created_at) <= endDate;
      });
    }

    // Client-side sort for metadata fields
    if (!API_SORT_FIELDS.has(sortBy)) {
      const sorted = [...result];
      const asc = sortOrder === 'asc';

      const getMetaValue = (note: NoteOut, field: NoteSortField): number => {
        if (field === 'is_favorite') return note.is_favorite ? 1 : 0;
        const webm = note.metadata?.media?.webm;
        switch (field) {
          case 'duration':
            return webm?.duration ?? -1;
          default:
            return 0;
        }
      };

      sorted.sort((a, b) => {
        const va = getMetaValue(a, sortBy);
        const vb = getMetaValue(b, sortBy);
        // Push items without metadata to the end regardless of sort direction
        if (va === -1 && vb === -1) return 0;
        if (va === -1) return 1;
        if (vb === -1) return -1;
        return asc ? va - vb : vb - va;
      });

      return sorted;
    }

    return result;
  }, [
    notes,
    dateRange.start,
    dateRange.end,
    sortBy,
    sortOrder,
    isOrgMode,
    noteIdFilter,
    searchQuery,
    processingType,
    selectedTags,
    isFavoritesView,
  ]);

  // Progressive rendering: only show a slice of filtered notes
  const visibleNotes = useMemo(() => filteredNotes.slice(0, visibleCount), [filteredNotes, visibleCount]);
  const hasMore = visibleCount < filteredNotes.length;

  // Reset visible count when filters, sort, or view mode change
  useEffect(() => {
    setVisibleCount(NOTES_PER_BATCH);
  }, [searchQuery, selectedFilters, sortBy, sortOrder, selectedTags, dateRange, viewMode, isFavoritesView]);

  // IntersectionObserver to load more notes when sentinel is visible
  const loadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + NOTES_PER_BATCH, filteredNotes.length));
  }, [filteredNotes.length]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          loadMore();
        }
      },
      { root: gridRef.current, rootMargin: '200px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading, loadMore]);

  const fetchNotes = async () => {
    setIsApiLoading(true);
    try {
      const commonFilters = {
        ...(searchQuery && { keywords: searchQuery }),
        ...(processingType && { processing_type: processingType as ProcessingTypeEnum }),
        ...(selectedTags.length > 0 && { tags: selectedTags.join(',') }),
        ...(isFavoritesView && { favorite: true }),
      };

      const data = shared
        ? await fetchSearchNotes({
            headers: { authorization: `Bearer ${userToken}` },
            queryParams: {
              shared: true,
              limit: 200,
              sort_by: apiSortBy as SortByEnum,
              sort_order: apiSortOrder as SortOrderEnum,
              ...commonFilters,
            },
          })
        : await fetchGetMyNotesDirect({
            headers: { authorization: `Bearer ${userToken}` },
            queryParams: {
              sort_by: apiSortBy,
              sort_order: apiSortOrder,
              ...commonFilters,
            },
          });

      setApiNotes(data.notes);
      setIsApiLoading(false);
    } catch (error) {
      errorReporter.expected('Failed to fetch notes', error, { feature: 'notes', shared });
      showAlert('Something went wrong while fetching notes. Try again later.', 'error');
      setIsApiLoading(false);
    }
  };

  useEffect(() => {
    if (isOrgMode) return;
    fetchNotes();
    // Only re-fetch when API-relevant params change.
    // Metadata sort fields (duration/resolution/size) are handled client-side
    // in filteredNotes memo, so they don't trigger a refetch.
  }, [
    userToken,
    apiSortBy,
    apiSortOrder,
    searchQuery,
    processingType,
    selectedTags,
    showAlert,
    isFavoritesView,
    shared,
    isOrgMode,
  ]);

  const handleFavoriteToggle = useCallback(
    (noteId: string, newValue: boolean) => {
      if (isOrgMode) {
        // Org notes are owned by React Query cache; refetch to reflect favorite changes
        refetchOrgNotes();
        return;
      }
      if (isFavoritesView && !newValue) {
        setApiNotes((prev) => prev.filter((note) => note.id !== noteId));
      } else {
        setApiNotes((prev) => prev.map((note) => (note.id === noteId ? { ...note, is_favorite: newValue } : note)));
      }
    },
    [isFavoritesView, isOrgMode, refetchOrgNotes],
  );

  const handleDelete = useCallback(
    async (noteId: string) => {
      if (!noteId) return;
      // In org mode, the per-note menu action removes the note from the team
      // rather than deleting it entirely.
      if (isOrgMode) {
        onRemoveFromOrganization?.(noteId);
        return;
      }
      try {
        await deleteNote(
          {
            headers: { authorization: `Bearer ${userToken}` },
            pathParams: { noteId },
          },
          {
            onSuccess: () => {
              setApiNotes((prevNotes) => prevNotes.filter((note) => note.id !== noteId));
              showAlert('Note archived successfully.', 'success');
            },
            onError: (error) => {
              showAlert(getDeleteNoteErrorMessage(error), 'error');
            },
          },
        );
      } catch {
        // mutateAsync always rejects on failure — onError above handles user feedback.
      }
    },
    [deleteNote, userToken, showAlert, isOrgMode, onRemoveFromOrganization],
  );

  // Open the "Move to folder" dialog for a note, defaulting to its current folder.
  const handleOpenMoveToFolder = useCallback(
    (noteId: string) => {
      const current = orgFolders.find((f) => f.note_ids.includes(noteId));
      setMoveNoteId(noteId);
      setMoveTargetFolderId(current?.id ?? 'general');
      setMoveDialogOpen(true);
    },
    [orgFolders],
  );

  const handleConfirmMoveToFolder = async () => {
    if (!moveNoteId || !organizationId) return;
    setIsMovingFolder(true);
    try {
      await reconcileNoteFolder(orgFolders, organizationId, moveNoteId, moveTargetFolderId, {
        addNoteToFolder,
        removeNoteFromFolder,
      });
      await refetchOrgFolders();
      showAlert(moveTargetFolderId === 'general' ? 'Removed from folder.' : 'Note moved to folder.', 'success');
      setMoveDialogOpen(false);
      setMoveNoteId(null);
    } catch (error: unknown) {
      showAlert(getApiErrorMessage(error, 'Failed to update folder. Please try again.'), 'error');
    } finally {
      setIsMovingFolder(false);
    }
  };

  // The created folder is returned so we can refresh the list and pre-select it
  // back in the Add-to-folder dialog.
  const handleFolderCreated = async (folder?: OrganizationFolder) => {
    setCreateFolderOpen(false);
    await refetchOrgFolders();
    if (folder) setMoveTargetFolderId(folder.id);
  };

  const handleListSortChange = (field: NoteSortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      // Default to desc for date and starred, asc for everything else
      setSortOrder(field === 'created_at' || field === 'is_favorite' ? 'desc' : 'asc');
    }
  };

  const renderSkeletons = () => {
    if (isListView) {
      return Array.from({ length: 8 }).map((_, index) => (
        <SkeletonListRow key={index} showThumbnails={listViewPreferences.showThumbnails} />
      ));
    }
    return Array.from({ length: 8 }).map((_, index) => <SkeletonCard key={index} />);
  };

  const renderNotes = () => {
    if (isLoading) {
      return renderSkeletons();
    }

    // Team notes failed to load AND we have nothing cached to show — render
    // nothing. The page above already shows a retryable error; falling through
    // would stack the "No notes" empty-state illustration underneath it and read
    // as "this team has no notes". Guarded on the list being empty so a FAILED
    // REFETCH (React Query keeps `data` and sets `error`) doesn't blank a grid
    // that is still perfectly good — e.g. removing a note on a flaky connection.
    if (isOrgMode && orgNotesError && filteredNotes.length === 0) {
      return null;
    }

    if (filteredNotes && filteredNotes.length > 0) {
      // Team view: owner/admin can act on any team note, and a member can always
      // act on a note they created — team unshare is never owner-only.
      // Filing into folders stays a team-management action.
      const canManageOrgNote = (creatorUserId?: string | null) =>
        canRemoveFromOrganization || (!!userData?.id && creatorUserId === userData.id);
      const orgMenuProps = isOrgMode
        ? {
            menuActionLabel: 'Remove from team',
            deleteModalTitle: 'Remove note?',
            deleteModalText: 'This will remove the note from the team. The note itself will not be deleted.',
            deleteButtonText: 'Remove',
            onMoveToFolder: canRemoveFromOrganization ? handleOpenMoveToFolder : undefined,
          }
        : {};
      if (isListView) {
        return visibleNotes.map(
          ({
            id,
            title,
            tags,
            transcript_url,
            video_url,
            created_at,
            processing_type,
            thumbnail_list_url,
            thumbnail_url,
            creator_user_id,
            shared_by,
            is_favorite,
            summary,
            metadata,
          }: NoteOut) => (
            <NoteListRow
              key={id}
              id={id}
              title={title}
              tags={tags}
              transcript_url={transcript_url}
              video_url={video_url}
              created_at={created_at}
              processing_type={processing_type}
              thumbnail_url={thumbnail_list_url || thumbnail_url}
              creator_user_id={creator_user_id}
              shared_by={shared_by}
              is_favorite={is_favorite}
              summary={summary}
              metadata={metadata}
              showThumbnails={listViewPreferences.showThumbnails}
              showTags={listViewPreferences.showTags}
              onDelete={!isOrgMode || canManageOrgNote(creator_user_id) ? handleDelete : undefined}
              onMoveToTeam={canMoveToTeam ? handleOpenTeamShare : undefined}
              onFavoriteToggle={handleFavoriteToggle}
              navigateUrl={isOrgMode ? `${RouteMap.NOTE}/${id}?org=${organizationId}` : undefined}
              isTeamNote={teamNoteIds.has(id)}
              {...orgMenuProps}
            />
          ),
        );
      }

      return visibleNotes.map(
        ({
          id,
          title,
          tags,
          transcript_url,
          video_url,
          created_at,
          processing_type,
          thumbnail_card_url,
          thumbnail_url,
          creator_user_id,
          shared_by,
          is_favorite,
        }: NoteOut) => (
          // Key on the stable note id only. An index-based key shifts for every
          // card at or after a removal, unmounting and remounting memo'd NoteCards
          // and re-firing their thumbnail resolution and per-card fetches.
          <NoteCard
            key={id}
            noteId={id}
            title={title || 'Untitled'}
            tags={tags}
            transcript={transcript_url || ''}
            onConfirmDelete={!isOrgMode || canManageOrgNote(creator_user_id) ? handleDelete : undefined}
            onMoveToTeam={canMoveToTeam ? handleOpenTeamShare : undefined}
            hasVideo={!!video_url}
            createdAt={created_at}
            processingType={processing_type}
            thumbnailUrl={thumbnail_card_url || thumbnail_url || ''}
            creatorUserId={creator_user_id}
            sharedBy={shared_by}
            isFavorite={is_favorite}
            onFavoriteToggle={handleFavoriteToggle}
            navigateUrl={isOrgMode ? `${RouteMap.NOTE}/${id}?org=${organizationId}` : undefined}
            isTeamNote={teamNoteIds.has(id)}
            {...orgMenuProps}
          />
        ),
      );
    }

    const hasActiveFilters =
      !!searchQuery ||
      selectedTags.length > 0 ||
      !!processingType ||
      !!dateRange.start ||
      !!dateRange.end ||
      isFavoritesView;

    return (
      <Stack width="100%">
        <Grid size={12} marginTop={10} textAlign="center">
          <img width="150px" src={emptyNotes} alt="Illustration showing no available notes" />
        </Grid>

        <Grid size={12} marginTop={1} textAlign="center">
          <Typography variant="h6" fontWeight={400}>
            {hasActiveFilters
              ? 'No notes found.'
              : isOrgMode
                ? `No notes in ${folderName ?? 'this folder'}.`
                : shared
                  ? 'No one has shared any notes with you yet.'
                  : "You don't have any notes yet."}
          </Typography>
        </Grid>
      </Stack>
    );
  };

  return (
    <Stack
      justifyContent="flex-start"
      maxWidth={isMobile ? '100%' : '1620px'}
      width="100%"
      height={`calc(100% - ${headerHeight}px)`}
      overflow="hidden"
    >
      <Stack
        gap={2}
        sx={{ margin: '26px 0' }}
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        flexShrink={0}
        flexWrap="wrap"
      >
        <Stack flexDirection="row" alignItems="center" gap={1}>
          {isListView && <ListViewControls />}
          <ViewToggle />
          <DateRangeFilter />
          {!planLoading && features.aiTagging && (
            <FilterTags tagCounts={accurateTagCounts} onOpen={() => setTagFilterOpened(true)} />
          )}
          <ExpandableSearch placeholder={shared || isOrgMode ? 'Search notes or people...' : undefined} />
        </Stack>
        {!planLoading && !features.aiTagging && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`${RouteMap.PLAN}#${RouteMap.PLAN_ANCHOR}`)}
            sx={{
              textTransform: 'none',
              borderRadius: '20px',
              px: 2,
              alignSelf: 'flex-start',
            }}
          >
            Upgrade to use AI tagging
          </Button>
        )}
        {!isLoading && filteredNotes.length > 0 && (
          <NoteListHeader
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleListSortChange}
            showThumbnails={isListView && listViewPreferences.showThumbnails}
          />
        )}
      </Stack>

      <Box
        ref={gridRef}
        flex={1}
        minHeight={0}
        pb={2}
        sx={
          isListView
            ? {
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
                overflowX: 'hidden',
                width: '100%',
                minWidth: 0,
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }
            : {
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: isMobile ? 2 : 3,
                alignContent: 'flex-start',
                overflow: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }
        }
      >
        {renderNotes()}
        {hasMore && !isLoading && (
          <Box ref={sentinelRef} sx={{ width: '100%', height: 1, flexShrink: 0, gridColumn: '1 / -1' }} />
        )}
      </Box>

      {isOrgMode && organizationId && (
        <>
          <Dialog
            open={moveDialogOpen}
            onClose={() => (isMovingFolder ? null : setMoveDialogOpen(false))}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>Move to folder</DialogTitle>
            <DialogContent>
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                <FormControl fullWidth>
                  <InputLabel id="move-folder-label">Folder</InputLabel>
                  <Select
                    labelId="move-folder-label"
                    label="Folder"
                    value={moveTargetFolderId}
                    onChange={(e) => setMoveTargetFolderId(e.target.value)}
                    disabled={isMovingFolder}
                  >
                    <MenuItem value="general">General (no folder)</MenuItem>
                    {orgFolders.map((folder) => (
                      <MenuItem key={folder.id} value={folder.id}>
                        {folder.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<CreateNewFolderOutlinedIcon />}
                  onClick={() => setCreateFolderOpen(true)}
                  disabled={isMovingFolder}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  New folder
                </Button>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setMoveDialogOpen(false)} disabled={isMovingFolder}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleConfirmMoveToFolder} disabled={isMovingFolder}>
                Save
              </Button>
            </DialogActions>
          </Dialog>

          {orgData && (
            <FolderShareModal
              open={createFolderOpen}
              onClose={() => setCreateFolderOpen(false)}
              organizationId={organizationId}
              memberEmails={orgData.member_emails}
              ownerEmail={orgData.owner_email}
              onSaved={handleFolderCreated}
            />
          )}
        </>
      )}

      {/* Move a personal note into / out of the user's team (same modal the note header uses). */}
      {teamModalTarget && (
        <TeamShareFolderModal
          open
          onClose={() => setTeamModalTarget(null)}
          organization={teamModalTarget.organization}
          noteId={teamModalTarget.noteId}
        />
      )}
    </Stack>
  );
};

export default NotesContent;
